import React, { useState, useEffect } from 'react';
import type { ApiClient } from './types';
import { useAuth } from './auth/AuthContext';
import Sidebar from './components/Sidebar';
import UploadArea from './components/UploadArea';
import ResultsContainer from './components/ResultsContainer';
import useAnalysis from './hooks/useAnalysis';
import useResultState from './hooks/useResultState';

interface AppProps {
  apiClient: ApiClient;
}

const App: React.FC<AppProps> = ({ apiClient }) => {
  const { isAuthenticated, loading: authLoading, logout, user, displayName } = useAuth();
  
  const {
    result,
    videoObjectUrl,
    audioObjectUrl,
    showingResults,
    setShowingResults,
    handleSelectHistoryItem,
    setResultWithUrls,
    reset: resetResultState,
  } = useResultState();
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const {
    availableCategories,
    fetchCategories,
    analysisHistory,
    submit: submitAnalysis,
    updateHistoryChat,
    loading,
    loadingMessage,
    error,
    setError,
    selectedCategory,
    setSelectedCategory,
    categoryConfig,
  } = useAnalysis(apiClient);
  

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchCategories();
    }
  }, [isAuthenticated, authLoading, fetchCategories]);

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    resetResultState();
    setError(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed with error:', error);
    }
  };

  const handleUploadSubmit = async ({ selectedCategory: sCategory, inputText: sInputText, selectedFiles: sSelectedFiles, formData: sFormData }: { selectedCategory: string; inputText: string; selectedFiles: File[]; formData: Record<string, string | boolean> }) => {
    setError(null);
    setShowingResults(true);
    try {
      const augmented = await submitAnalysis({ selectedCategory: sCategory, inputText: sInputText, selectedFiles: sSelectedFiles, formData: sFormData });
      setResultWithUrls(augmented);
    } catch (err) {
      if (err instanceof Error) setError(err.message);
      else setError('An unexpected error occurred');
    }
  };

  const resetForm = () => {
    resetResultState();
    setError(null);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-brand-yellow border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-muted">Authenticating...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary flex">
      <Sidebar
        sidebarOpen={sidebarOpen}
        onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        analysisHistory={analysisHistory}
        onSelectHistoryItem={handleSelectHistoryItem}
        onNewAnalysis={resetForm}
        displayName={displayName}
        user={user}
        onLogout={handleLogout}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        <div className="flex-1 flex flex-col items-center">
        {!showingResults ? (
          <UploadArea
            selectedCategory={selectedCategory}
            availableCategories={availableCategories}
            onSelectCategory={handleCategorySelect}
            loading={loading}
            loadingMessage={loadingMessage}
            error={error}
            onSubmit={handleUploadSubmit}
            displayName={displayName}
            userName={user?.name}
          />
        ) : (
          <ResultsContainer
            result={result}
            videoObjectUrl={videoObjectUrl}
            audioObjectUrl={audioObjectUrl}
            onBack={resetForm}
            loading={loading}
            loadingMessage={loadingMessage}
            error={error}
            categoryName={categoryConfig?.name}
            apiClient={apiClient}
            updateHistoryChat={updateHistoryChat}
          />
        )}
        </div>
      </div>
    </div>
  );
};

export default App;
